/**
 * Utility functions for safe navigation to prevent DataCloneError
 */

/**
 * Safely navigate with error handling for DataCloneError
 * @param {Function} navigate - React Router navigate function
 * @param {string} path - Path to navigate to
 * @param {Object} options - Navigation options (state, replace, etc.)
 */
export const safeNavigate = (navigate, path, options = {}) => {
  try {
    // Ensure state is serializable by removing any functions or complex objects
    if (options.state) {
      options.state = JSON.parse(JSON.stringify(options.state));
    }
    navigate(path, options);
  } catch (error) {
    if (error.name === 'DataCloneError') {
      console.warn('DataCloneError caught, navigating without state:', error);
      // Navigate without state to avoid the error
      const { state, ...safeOptions } = options;
      navigate(path, safeOptions);
    } else {
      console.error('Navigation error:', error);
      // Fallback to simple navigation
      navigate(path);
    }
  }
};

/**
 * Clean state object to ensure it's serializable
 * @param {Object} state - State object to clean
 * @returns {Object} - Cleaned state object
 */
export const cleanState = (state) => {
  if (!state || typeof state !== 'object') {
    return state;
  }
  
  try {
    // Remove functions, DOM elements, and other non-serializable data
    const cleaned = {};
    for (const [key, value] of Object.entries(state)) {
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        cleaned[key] = value;
      } else if (value === null || value === undefined) {
        cleaned[key] = value;
      } else if (Array.isArray(value)) {
        cleaned[key] = value.filter(item => 
          typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean'
        );
      } else if (typeof value === 'object') {
        // Recursively clean nested objects
        cleaned[key] = cleanState(value);
      }
    }
    return cleaned;
  } catch (error) {
    console.warn('Error cleaning state, returning empty object:', error);
    return {};
  }
};